/**
 * Shared presentational parts for the Small Power tab:
 *   SummaryCard — stat tile.
 *   Section     — header + "Add row" button + body.
 *   ItemTable   — editable table for receptacles / luminaires / switches.
 */
import { Plus, Trash } from "@phosphor-icons/react";

export function SummaryCard({ label, value, testid }) {
  return (
    <div className="bg-[#0a0d12] border border-white/10 p-3" data-testid={testid}>
      <div className="label-tag text-[10px]">{label}</div>
      <div className="font-display text-xl font-bold text-[#00E5FF]">{value}</div>
    </div>
  );
}

export function Section({ title, icon, onAdd, children, testid }) {
  return (
    <div className="mb-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">{icon}<h3 className="font-display text-lg font-bold">{title}</h3></div>
        <button
          onClick={onAdd}
          className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1"
          data-testid={`${testid}-add`}
        >
          <Plus size={14} /> Add row
        </button>
      </div>
      {children}
    </div>
  );
}

export function ItemTable({ bucket, rows, types, columns, onEdit, onType, onRemove }) {
  if (!rows?.length) {
    return (
      <div className="border border-white/10 bg-[#0a0d12] p-4 text-sm text-gray-500 font-mono" data-testid={`${bucket}-empty`}>
        No {bucket} yet — click "Add row" or "Auto-install per code".
      </div>
    );
  }
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid={`${bucket}-table`}>
      <table className="w-full">
        <thead className="bg-[#0a0d12] border-b border-white/10">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`text-left text-[10px] uppercase tracking-widest text-gray-500 px-3 py-2 font-mono ${c.width}`}>{c.label}</th>
            ))}
            <th className="w-12"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`${bucket}-row-${r.id}`}>
              {columns.map((c) => (
                <td key={c.key} className="px-2 py-1">
                  {c.isTypeSelect ? (
                    <select
                      value={r.type}
                      onChange={(e) => onType(bucket, r.id, e.target.value)}
                      className="input-base text-xs w-full"
                      data-testid={`${bucket}-${r.id}-type`}
                    >
                      {(types || []).map((t) => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={c.type || "text"}
                      value={r[c.key] ?? ""}
                      onChange={(e) => onEdit(bucket, r.id, c.key, e.target.value)}
                      className="input-base text-xs w-full"
                      data-testid={`${bucket}-${r.id}-${c.key}`}
                    />
                  )}
                </td>
              ))}
              <td className="px-2 py-1">
                <button
                  onClick={() => onRemove(bucket, r.id)}
                  className="text-gray-500 hover:text-red-400"
                  data-testid={`${bucket}-${r.id}-remove`}
                >
                  <Trash size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
